import { BodyPrimary, BodySecondary } from '@liveauctioneers/hammer-ui-core/text';
import { FormattedMessage } from '@liveauctioneers/hammer-ui-core/intl';
import { getBidderId } from '@/redux/modules/account/user/user.selectors';
import { getBidLimitBalance } from '@/redux/modules/bidLimit/bidLimit.selectors';
import { getCatalogRegistration } from '@/redux/modules/catalog/registration/catalogRegistration.selectors';
import { getItemSummaryCurrency, getItemSummaryStartPrice } from '@/redux/modules/item/summary/itemSummary.selectors';
import {
    getLiveCatalogCurrentItemId,
    getLiveItemDataCurrentAskPrice,
    getLiveItemDataLeadingBidder,
} from '@/redux/modules/console';
import { Info, Question } from '@liveauctioneers/hammer-ui-core/icons';
import { openBidLimitExceededModal, openWaitingForApprovalModal } from '@/redux/modules/modal';
import { useAppDispatch, useAppSelector } from '@/redux/hooks';
import BiddingConsoleButton from '../BiddingConsoleButton/BiddingConsoleButton';
import FormattedCurrency from '@/components/Formatted/FormattedCurrency';
import styled from 'styled-components';

type Props = {
    biddingLocked: boolean;
    catalogId: number;
    mobile?: boolean;
};

const ButtonGroup = ({ biddingLocked, catalogId }: Props) => {
    const dispatch = useAppDispatch();

    const catalogRegistration = useAppSelector((state) => getCatalogRegistration(state, catalogId));
    const itemId = useAppSelector((state) => getLiveCatalogCurrentItemId(state, catalogId));
    const bidLimitBalance = useAppSelector((state) => getBidLimitBalance(state, catalogId));
    const currentItemStartPrice = useAppSelector((state) => getItemSummaryStartPrice(state, itemId));
    const currentItemCurrency = useAppSelector((state) => getItemSummaryCurrency(state, itemId));
    const currentAskPrice = useAppSelector((state) => getLiveItemDataCurrentAskPrice(state, itemId));
    const leadingBidder = useAppSelector((state) => getLiveItemDataLeadingBidder(state, itemId));
    const bidderId = useAppSelector((state) => getBidderId(state));

    const ask = currentAskPrice || currentItemStartPrice;
    const overBidLimit = ask > bidLimitBalance;
    const winning = bidderId === leadingBidder;

    if (catalogRegistration.pending) {
        return (
            <>
                <BiddingConsoleButton
                    biddingLocked={biddingLocked}
                    catalogId={catalogId}
                    itemId={itemId}
                />
                <SplitButton onClick={() => dispatch(openWaitingForApprovalModal(catalogId))}>
                    <BodyPrimary>
                        <FormattedMessage id="bidding_console.console.button.waiting" />
                    </BodyPrimary>
                    &nbsp;
                    <Question
                        className="mb-2xs inline-block"
                        size="sm"
                        style="fill"
                    />
                </SplitButton>
            </>
        );
    }

    if (catalogRegistration.limited) {
        if (overBidLimit && !winning) {
            return (
                <>
                    <BiddingConsoleButton
                        biddingLocked={biddingLocked}
                        catalogId={catalogId}
                        itemId={itemId}
                    />
                    <SplitButton onClick={() => dispatch(openBidLimitExceededModal(catalogId))}>
                        <LimitExceededCurrencyContainer>
                            <BodyPrimary>
                                <FormattedMessage
                                    id="bidding_console.console.button.limit_exceeded"
                                    values={{
                                        limit: (
                                            <FormattedCurrency
                                                key="bidding-console-button-limit-exceeded-limit"
                                                noFraction
                                                sourceCurrency={currentItemCurrency}
                                                value={catalogRegistration.bidLimit}
                                            />
                                        ),
                                    }}
                                />
                            </BodyPrimary>
                        </LimitExceededCurrencyContainer>
                        &nbsp;
                        <Info
                            className="mb-2xs inline-block"
                            size="sm"
                            style="fill"
                        />
                    </SplitButton>
                </>
            );
        }

        if (!overBidLimit) {
            return (
                <>
                    <BiddingConsoleButton
                        biddingLocked={biddingLocked}
                        catalogId={catalogId}
                        itemId={itemId}
                    />
                    <SplitButton>
                        <BodySecondary>
                            <FormattedMessage
                                id="bidding_console.console.button.approved_limited_short"
                                values={{
                                    balance: (
                                        <FormattedCurrency
                                            key="bidding-console-button-approved-limited-short-balance"
                                            noFraction
                                            sourceCurrency={currentItemCurrency}
                                            value={bidLimitBalance}
                                        />
                                    ),
                                    limit: (
                                        <FormattedCurrency
                                            key="bidding-console-button-approved-limited-short-limit"
                                            noFraction
                                            sourceCurrency={currentItemCurrency}
                                            value={catalogRegistration.bidLimit}
                                        />
                                    ),
                                }}
                            />
                        </BodySecondary>
                    </SplitButton>
                </>
            );
        }
    }

    return (
        <BiddingConsoleButton
            biddingLocked={biddingLocked}
            catalogId={catalogId}
            itemId={itemId}
        />
    );
};

export default ButtonGroup;

const LimitExceededCurrencyContainer = styled.span`
    font-family: ${({ theme }) => theme.typography.fontPrimaryStrong};
`;

const SplitButton = styled.div.attrs({ role: 'button' })`
    align-items: center;
    color: ${({ theme }) => theme.colors.grey100};
    cursor: pointer;
    display: block;
    flex: 0 1 auto;
    font-size: 14px;
    margin: 10px 0 0 0;
    text-align: left;

    svg {
        color: ${({ theme }) => theme.colors.grey100};
    }
`;
